import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { accountName } from './hooks'
import type { CrmAccount } from './types'
import { Spinner, BusyLabel } from '../components/Spinner'

/** Centered dialog, or a right-hand sheet with `side`. Escape and the backdrop close it. */
export function Modal({
  title,
  onClose,
  children,
  footer,
  side = false,
  wide = false,
}: {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  side?: boolean
  wide?: boolean
}) {
  const { t } = useTranslation('dynamicLists')
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A dropdown inside the sheet marks its own Escape as handled.
      if (e.key === 'Escape' && !e.defaultPrevented) onClose()
    }
    window.addEventListener('keydown', onKey)
    box.current?.focus()
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className={`dl-overlay ${side ? 'dl-overlay--side' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={box}
        className={`dl-modal ${side ? 'dl-modal--side' : ''} ${wide ? 'dl-modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        <header className="dl-modal-head">
          <h2 className="dl-h2">{title}</h2>
          <button type="button" className="dl-iconbtn" onClick={onClose} aria-label={t('close')}>
            <FeatherIcon name="x" size={16} />
          </button>
        </header>
        <div className="dl-modal-body">{children}</div>
        {footer && <footer className="dl-modal-foot">{footer}</footer>}
      </div>
    </div>
  )
}

export function ConfirmDialog({
  title,
  children,
  confirm,
  danger = false,
  onConfirm,
  onClose,
}: {
  title: string
  children: ReactNode
  confirm: string
  danger?: boolean
  onConfirm: () => Promise<void>
  onClose: () => void
}) {
  const { t } = useTranslation('dynamicLists')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const run = async () => {
    setBusy(true)
    setError('')
    try {
      await onConfirm()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className={`dl-btn ${danger ? 'dl-btn--danger' : 'dl-btn--primary'}`} disabled={busy} onClick={() => void run()}>
            <BusyLabel busy={busy} label={t('saving')}>{confirm}</BusyLabel>
          </button>
        </>
      }
    >
      <div className="dl-confirm">{children}</div>
      {error && <p className="dl-error">{error}</p>}
    </Modal>
  )
}

const fold = (s: string) => s.toLocaleLowerCase()

/** One OnlyFans Account, chosen from a searchable dropdown. */
export function AccountSelect({
  accounts,
  value,
  onChange,
}: {
  accounts: CrmAccount[] | null
  value: string
  onChange: (id: string) => void
}) {
  const { t } = useTranslation('dynamicLists')
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const root = useRef<HTMLDivElement>(null)
  const current = accounts?.find((a) => a.account_id === value) ?? null

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const shown = useMemo(() => {
    const query = fold(q.trim())
    return (accounts ?? [])
      .filter((a) => !query || [a.profile_name, a.username, a.account_id].some((s) => fold(s || '').includes(query)))
      .sort((a, b) => accountName(a).localeCompare(accountName(b), undefined, { sensitivity: 'base' }))
  }, [accounts, q])

  const pick = (id: string) => {
    onChange(id)
    setOpen(false)
    setQ('')
  }

  return (
    <div className="dl-accsel" ref={root}>
      <button
        type="button"
        className="dl-accsel-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        disabled={!accounts}
      >
        {current?.avatar_url ? <img src={current.avatar_url} alt="" className="dl-avatar" /> : <FeatherIcon name={current ? 'user' : 'layers'} size={14} />}
        <span className="dl-ellipsis">{current ? accountName(current) : accounts ? t('account.all') : <Spinner size={12} label={t('loading')} />}</span>
        {current?.username && <span className="dl-muted dl-ellipsis">@{current.username}</span>}
        <FeatherIcon name="chevron-down" size={14} className="dl-accsel-caret" />
      </button>
      {open && (
        <div className="dl-pop" role="listbox" aria-label={t('account.label')}>
          <label className="dl-search">
            <FeatherIcon name="search" size={14} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('account.search')} />
          </label>
          <div className="dl-pop-list">
            {!q.trim() && (
              <button
                type="button"
                role="option"
                aria-selected={value === ''}
                className={`dl-pop-item ${value === '' ? 'is-on' : ''}`}
                onClick={() => pick('')}
              >
                <FeatherIcon name="layers" size={14} className="dl-avatar dl-avatar--icon" />
                <span className="dl-ellipsis">{t('account.all')}</span>
              </button>
            )}
            {shown.length === 0 && <p className="dl-muted dl-pad">{t('account.none')}</p>}
            {shown.map((a) => (
              <button
                key={a.account_id}
                type="button"
                role="option"
                aria-selected={a.account_id === value}
                className={`dl-pop-item ${a.account_id === value ? 'is-on' : ''}`}
                onClick={() => pick(a.account_id)}
              >
                {a.avatar_url ? <img src={a.avatar_url} alt="" className="dl-avatar" /> : <span className="dl-avatar dl-avatar--blank" />}
                <span className="dl-ellipsis">{accountName(a)}</span>
                {a.username && <span className="dl-muted dl-ellipsis">@{a.username}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
